import { ContextualMenuItemType, DefaultButton } from '@fluentui/react';
import MultiButton, { MultiButtonItem } from '@v2/components/base/MultiButton';
import classes from 'classnames';
import * as React from 'react';
import { useState } from 'react';
import ConfirmDialog from "src/components/base/ConfirmDialog";
import { Button } from 'src/components/Flowsteps/base/Flowstep';
import { BaseEditorState } from '@v2/components/flowsteps/editors/common/states/base-editor-state';
import { FlowstepButtonsState } from './flowstep-buttons-state';
import { useStyles } from './FlowstepButtons.styles';
import { useHistory } from "react-router-dom";

interface FlowStepButtonsProps {
  disabled: boolean;
  saveIsDisabled?: boolean;
  runAndSaveIsDisabled?: boolean;
  className?: string;
  editorState: BaseEditorState;
  onRunClick?: () => Promise<void>;
}

const withAction = ({ items, ...rest }: { items: any; }, action: string) => ({ ...rest, items: items.map((x: any) => ({ ...x, action })) });

export const FlowstepButtons: React.FC<FlowStepButtonsProps> = (props) => {
  const {
    disabled,
    saveIsDisabled,
    runAndSaveIsDisabled,
    className,
    editorState,
    onRunClick
  } = props;

  const [state] = useState(() => new FlowstepButtonsState(editorState));
  const [showDeleteConfirmation, setShowDeleteConfirmation] = React.useState(false);
  const styles = useStyles();
  const history = useHistory();


  const runItems: MultiButtonItem[] = [
    state.allowEditExisting && {
      key: Button.RunAndSave,
      text: 'Run and save',
    },
    !state.hasPersonalPlan && {
      key: Button.Run,
      text: 'Run without save'
    }
  ].filter(Boolean);

  const saveSubMenuProps = { items: state.flowsMoveTo };

  const saveItems: MultiButtonItem[] = [
    {
      key: Button.Save,
      text: 'Save',
    },
    state.canSaveCopy && !!state.flowsMoveTo.length && {
      key: 'copy-to',
      text: 'Save copy to flow',
      tooltip: 'Adds the copy of this flow step to a different flow',
      subMenuProps: withAction(saveSubMenuProps, Button.SaveCopy),
    },
    !!state.flowstep && !!state.flowsMoveTo.length && {
      key: Button.Move,
      text: 'Move to flow',
      tooltip: 'Moves this flow step to a different flow',
      subMenuProps: withAction(saveSubMenuProps, Button.Move),
    },
    state.canSaveCopy && { key: 'separator', itemType: ContextualMenuItemType.Divider },
    state.canSaveCopy && { key: Button.SaveCopy, text: 'Save copy' },
  ].filter(Boolean) as MultiButtonItem[];

  const handleRunClick = async (_: any, item: any) => {
    if (item.key === Button.Run) {
      onRunClick ? await onRunClick() : await editorState.runFlowstep();
    }
    if (item.key === Button.RunAndSave) {
      await editorState.saveFlowstep();
      onRunClick ? await onRunClick() : await editorState.runFlowstep();
    }
    history.goBack();
  }

  const handleSaveClick = async (_: any, item: any) => {
    if (item.key === Button.Save) {
      await editorState.saveFlowstep();
    }
    if (item.action === Button.SaveCopy) {
      const flowId = item.key || editorState.flowstep?.flowId;
      if (flowId) {
        await editorState.copyFlowstepToFlow(flowId);
      }
    }
    if (item.action === Button.Move) {
      const flowId = item.key;
      await editorState.moveFlowstepToFlow(flowId);
    }
    history.goBack();
  }

  const handleDeleteClick = async () => {
    await editorState.deleteFlowstep();
    history.goBack();
  }
  return (
    <div className={classes(styles.flowStepButtons, className)}>
      <MultiButton
        items={runItems}
        disabled={disabled || runAndSaveIsDisabled}
        primary
        onClick={handleRunClick}
      />
      <div className={styles.buttonGroup}>
        <DefaultButton
          className={styles.actionButton}
          disabled={!editorState.flowstep}
          onClick={() => setShowDeleteConfirmation(true)}
        >
          {Button.Delete}
        </DefaultButton>
        <MultiButton
          items={saveItems}
          disabled={disabled || saveIsDisabled}
          primary
          className={styles.actionButton}
          onClick={handleSaveClick} />

      </div>
      <ConfirmDialog
        title={'Delete the Flow Step?'}
        text={'Are you sure you want to permanently remove this flow step?'}
        hidden={!showDeleteConfirmation}
        onConfirm={handleDeleteClick}
        onDismiss={() => setShowDeleteConfirmation(false)}
      />
    </div >
  );
};
